import React, { useEffect, useMemo, useState } from "react";
import Button from "../../../shared/ui/Button";
import EnterpriseFilterSelect from "../../../shared/components/EnterpriseFilterSelect";
import { useTranslation } from "../../../i18n";
import { getViolationTypeOptions } from "../../../i18n/domain/cases";
import {
  calculateFreeTierQuote,
  calculateSubscriberQuote,
  formatEuro,
} from "../pricingCalculator";
import {
  createDeletionCase,
  formatCaseError,
} from "../casesApi";

const CreateDeletionCaseModal = ({
  open,
  onClose,
  selectedReviews,
  isProfessional = false,
  subscriberFreeRemaining = 0,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const violationOptions = useMemo(() => getViolationTypeOptions(t), [t]);
  const [violationType, setViolationType] = useState(violationOptions[0]?.value ?? "Spam");
  const [justification, setJustification] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setViolationType(violationOptions[0]?.value ?? "Spam");
    setJustification("");
    setError("");
    setSubmitting(false);
  }, [open, violationOptions]);

  const count = selectedReviews.length;
  const freeQuote = useMemo(() => calculateFreeTierQuote(count), [count]);
  const subQuote = useMemo(
    () => calculateSubscriberQuote(count, subscriberFreeRemaining),
    [count, subscriberFreeRemaining]
  );

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Justification (Begründung) is optional — submit is accepted when empty.
    const trimmed = justification.trim();
    if (!selectedReviews.length) {
      setError(t("cases.createModal.selectReview"));
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const created = await createDeletionCase({
        violationType,
        justification: trimmed,
        reviewIds: selectedReviews.map((r) => r.id),
      });
      await onSuccess?.(created);
      onClose?.();
    } catch (err) {
      setError(formatCaseError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="deletion-case-modal-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-[#0f172a]/40 backdrop-blur-[2px]"
        aria-label={t("common.close")}
        onClick={onClose}
      />
      <div className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[16px] border border-[#e8ecf0] bg-white p-6 shadow-[0_20px_50px_rgba(15,23,42,0.18)]">
        <h2
          id="deletion-case-modal-title"
          className="text-[20px] font-bold text-[#0f172a]"
        >
          {t("cases.createModal.title")}
        </h2>
        <p className="mt-2 text-[14px] leading-relaxed text-[#64748b]">
          {t("cases.createModal.subtitle", { count: selectedReviews.length })}
        </p>

        {isProfessional && subQuote.freeApplied > 0 ? (
          <div className="mt-4 rounded-[12px] border border-emerald-200 bg-emerald-50 px-4 py-3">
            <p className="text-[13px] font-semibold text-emerald-900">
              {t("cases.createModal.freeIncluded", { count: subQuote.freeApplied })}
            </p>
            <p className="mt-1 text-[12px] text-emerald-800/90">
              {t("cases.createModal.freeIncludedDetail")}
            </p>
          </div>
        ) : null}

        <ul className="mt-4 max-h-44 space-y-2 overflow-y-auto rounded-lg border border-[#e8ecf0] bg-[#f8fafc] p-3">
          {selectedReviews.map((r, index) => {
            const isFree =
              isProfessional && index < subQuote.freeApplied;
            return (
              <li
                key={r.id}
                className="flex items-start justify-between gap-2 text-[13px] text-[#334155]"
              >
                <div className="min-w-0 flex-1">
                  <span className="font-semibold text-[#0f172a]">
                    {r.author || t("cases.createModal.anonymous")}
                  </span>
                  <span className="text-[#94a3b8]"> · {r.rating}★ · </span>
                  <span className="line-clamp-1">{r.text || t("cases.createModal.noText")}</span>
                </div>
                {isProfessional ? (
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                      isFree
                        ? "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200"
                        : "bg-[#f1f5f9] text-[#64748b] ring-1 ring-[#e2e8f0]"
                    }`}
                  >
                    {isFree
                      ? t("cases.createModal.includedFree")
                      : formatEuro(subQuote.pricePerPaidReview)}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>

        <div className="mt-4 rounded-[12px] border border-[#006666]/20 bg-[#f0fafa] p-4">
          <p className="text-[12px] font-semibold uppercase tracking-wide text-[#64748b]">
            {t("cases.createModal.estimatedTotal")}
          </p>
          {!isProfessional ? (
            <div className="mt-2 flex items-end justify-between gap-4">
              <>
                <p className="text-[32px] font-bold leading-none text-[#006666]">
                  {formatEuro(freeQuote.pricePerReview)}
                </p>
                <p className="text-[12px] text-[#64748b]">{t("cases.createModal.perReview")}</p>
              </>
              <div className="text-right">
                <p className="text-[20px] font-bold text-[#0f172a]">
                  {formatEuro(freeQuote.total)}
                </p>
                <p className="text-[11px] text-[#64748b]">
                  {freeQuote.tier.label} · {count} {t("cases.createModal.reviews")}
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-2 space-y-2 text-[13px] text-[#475569]">
              {subQuote.freeApplied > 0 ? (
                <p className="font-semibold text-emerald-800">
                  {t("cases.createModal.freeLine", { count: subQuote.freeApplied })}
                </p>
              ) : null}
              {subQuote.paidCount > 0 ? (
                <p>
                  {t("cases.createModal.paidLine", {
                    count: subQuote.paidCount,
                    price: formatEuro(subQuote.pricePerPaidReview),
                  })}
                </p>
              ) : null}
              <p className="text-[22px] font-bold text-[#0f172a]">
                {formatEuro(subQuote.caseTotal)}
              </p>
            </div>
          )}
          <p className="mt-3 text-[11px] leading-relaxed text-[#64748b]">
            {t("cases.createModal.manualInvoicingNote")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div>
            <label
              htmlFor="violation-type"
              className="mb-1.5 block text-[13px] font-semibold text-[#0f172a]"
            >
              {t("cases.createModal.violationType")}
            </label>
            <EnterpriseFilterSelect
              id="violation-type"
              value={violationType}
              onChange={setViolationType}
              options={violationOptions}
              ariaLabel={t("cases.createModal.violationType")}
            />
          </div>

          <div>
            <label
              htmlFor="case-justification"
              className="mb-1.5 block text-[13px] font-semibold text-[#0f172a]"
            >
              {t("cases.createModal.justification")}
              <span className="ml-1 font-normal text-[#94a3b8]">
                ({t("cases.createModal.optional")})
              </span>
            </label>
            <textarea
              id="case-justification"
              rows={4}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder={t("cases.createModal.justificationPlaceholder")}
              className="w-full resize-y rounded-lg border border-[#e5e7eb] px-3 py-2 text-[14px] text-[#0f172a] outline-none focus:border-[#006666] focus:ring-1 focus:ring-[#006666]"
            />
          </div>

          {error && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800">
              {error}
            </p>
          )}

          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <button
              type="button"
              className="rounded-full px-4 py-2 text-[14px] font-medium text-[#64748b] hover:text-[#0f172a]"
              onClick={onClose}
              disabled={submitting}
            >
              {t("common.cancel")}
            </button>
            <Button
              type="submit"
              variant="teal"
              className="!h-11 !w-auto !rounded-full !px-6 !text-[14px]"
              disabled={submitting}
            >
              {submitting ? t("cases.createModal.submitting") : t("cases.createModal.submit")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateDeletionCaseModal;
